import "./Table.scss";
import React, { useEffect, useState } from "react";
import Arrow from "../Svg/Arrow";
import Tooltip from "../Tooltip/Tooltip";
import Pagination from "../Pagination/Pagination";
import noDataIcon from "../../../assets/img/icon-noData.svg";
import { FaArrowDown, FaArrowUp } from "react-icons/fa6";
import { PiSortAscendingBold } from "react-icons/pi";
import { PiSortDescendingBold } from "react-icons/pi";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

interface TableColumn {
  title: string;
  dataIndex: string;
  key: string;
  render?: any;
  width?: string;
  sortable?: boolean;
  isChecked?: boolean;
}

interface TableProps {
  data: any[];
  columns: TableColumn[];
  page?: number;
  handlePageNumberChange?: (pageNumber: number) => void;
  pagniation?: boolean;
  totalPages?: number;
  handleSortChange?: (sortOrder: any, sortBy: any) => void;
  errorMessage?: string;
  emptyIcon?: boolean;
  tableClass?: string;
  pageSize?: number;
  sortWithFrontend?: boolean;
  bgColor?: any;
}

const TableComponent: React.FC<TableProps> = ({
  data,
  columns,
  page,
  handlePageNumberChange,
  pagniation,
  totalPages,
  handleSortChange,
  errorMessage,
  emptyIcon = true,
  tableClass,
  pageSize,
  sortWithFrontend,
  bgColor,
}) => {
  const currentPage = page ?? 0;
  const total = totalPages ?? Math.ceil(data.length / (pageSize ?? 1));
  const defaultWidth = `${100 / columns.length}%`;

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: string;
  } | null>(
    sortWithFrontend
      ? { key: columns[0].dataIndex, direction: "ascending" }
      : null
  );

  const requestSort = (key: string, direction: "asc" | "desc") => {
    setSortConfig({ key, direction });
    if (sortWithFrontend) {
      setSortConfig({ key, direction });
    } else if (handleSortChange) {
      handleSortChange(direction, key);
    }
  };

  const sortedData = () => {
    if (!sortConfig) return data;

    const sorted = [...data].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  };

  const toggleDropdown = (columnKey: string) => {
    setActiveDropdown((prev) => (prev === columnKey ? null : columnKey));
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest("th")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  const renderTableHeader = () => (
    <thead>
      <tr>
        {columns.map((column) => (
          <th
            key={column.key}
            className="px-4 py-2 text-left cursor-pointer whitespace-nowrap relative table-header hover:text-teal-25"
            style={{ width: column.width || defaultWidth }}
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown(column.dataIndex);
            }}
          >
            <div className="flex items-center justify-between bg-gray-800 text-white p-2 rounded-lg hover:bg-darkcolor-800 hover:text-primary-400">
              <span className="text-sm font-bold flex items-center">
                {column.title}

                {sortConfig?.key === column.dataIndex && (
                  <span className="ml-2">
                    {sortConfig.direction === "asc" ? (
                      <FaArrowDown size={14} />
                    ) : (
                      <FaArrowUp size={14} />
                    )}
                  </span>
                )}
              </span>
              {column.sortable && (
                <div className="ml-2">
                  {sortConfig?.key === column.dataIndex ? (
                    sortConfig.direction === "asc" ? (
                      <IoIosArrowUp size={14} />
                    ) : (
                      <IoIosArrowDown size={14} />
                    )
                  ) : (
                    <IoIosArrowDown size={14} />
                  )}
                </div>
              )}
            </div>

            {activeDropdown === column.dataIndex && column.sortable && (
              <div className="absolute bg-darkcolor-800 left-0 mt-1 text-white border rounded-lg shadow-lg w-36 z-50">
                <div
                  className="cursor-pointer hover:bg-gray-100 p-2 flex items-center justify-between text-[16px] font-normal"
                  onClick={() => requestSort(column.dataIndex, "asc")}
                >
                  <div className="flex items-center gap-2">
                    <PiSortAscendingBold size={20} />
                    <span>A to Z</span>
                  </div>
                  {sortConfig?.key === column.dataIndex &&
                    sortConfig.direction === "asc" && (
                      <span className="text-primary-400">✔</span>
                    )}
                </div>

                <div
                  className="cursor-pointer hover:bg-gray-100 p-2 flex items-center justify-between text-[16px] font-normal"
                  onClick={() => requestSort(column.dataIndex, "desc")}
                >
                  <div className="flex items-center gap-2">
                    <PiSortDescendingBold size={20} />
                    <span>Z to A</span>
                  </div>
                  {sortConfig?.key === column.dataIndex &&
                    sortConfig.direction === "desc" && (
                      <span className="text-primary-400">✔</span>
                    )}
                </div>
              </div>
            )}
          </th>
        ))}
      </tr>
    </thead>
  );

  const renderFrontendTableBody = () => {
    const start = (currentPage - 1) * (pageSize || 1);
    const end = start + (pageSize || 1);

    return (
      <tbody>
        {sortedData()
          .slice(start, end)
          .map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className="px-4 py-2 text-neutral-25 text-[14px] text-left"
                  style={{ width: column.width }}
                >
                  {column.render
                    ? column.render(row[column.dataIndex], row)
                    : row[column.dataIndex]}
                </td>
              ))}
            </tr>
          ))}
      </tbody>
    );
  };

  const renderTableBody = () => (
    <tbody>
      {data.length > 0 ? (
        data.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {columns.map((column) => (
              <td
                key={column.key}
                className={`px-4 py-2 text-neutral-25 text-[14px] text-left `}
                style={{ width: column.width || defaultWidth }}
              >
                {column.render
                  ? column.render(row[column.dataIndex], row)
                  : row[column.dataIndex]}
              </td>
            ))}
          </tr>
        ))
      ) : (
        <tr>
          {emptyIcon ? (
            <td colSpan={columns.length} className="text-center py-10">
              <div className="flex flex-col items-center">
                <div className=" text-neutral-25 mb-4">
                  <img
                    src={noDataIcon}
                    alt="SiteName"
                    className="object-contain bg-img"
                  />
                </div>
                <span className="text-neutral-25 title">
                  {errorMessage ?? "No data found"}
                </span>
              </div>
            </td>
          ) : (
            <td colSpan={columns.length} className="py-4">
              <span className="text-neutral-25 title">
                {errorMessage ?? "No data found"}
              </span>
            </td>
          )}
        </tr>
      )}
    </tbody>
  );

  const columnsSort = (order: string, title: string) => {
    handleSortChange && handleSortChange(order, title);
  };

  const handlePageClick = (page: number) => {
    handlePageNumberChange && handlePageNumberChange(page);
  };

  return (
    <>
      <div style={(data.length >= 1 && columns.length > 6) ? { overflowX: "auto" } : {}}>
        <table
          className={`min-w-full rounded-[8px] bg-darkcolor-700 outline-[1px] ${tableClass} mb-[10px]`}
          style={bgColor ? bgColor : undefined}
        >
          {renderTableHeader()}
          {pageSize && pageSize > 0 && emptyIcon
            ? renderFrontendTableBody()
            : renderTableBody()}
        </table>
      </div>
      {pagniation && data.length > 0 && (
        <Pagination
          currentPage={currentPage ?? 0}
          totalPages={total}
          onPageChange={handlePageClick}
        />
      )}
    </>
  );
};

export default TableComponent;
